import * as React from 'react';
import { getReservationNotifications, sendNotificationOnAction } from '../api';
import { MessageBrokerInformationModel } from '@common/modelDefinition';
import { Reservation } from '@pages/newReservations/resController';
import { translate } from '@data/translations';
import ConfirmModal from '@components/confirmModal';
import { setModalContent } from '@components/modal';
import useStore from '@data/state/zustand';
import { Filters, filterListItems, ListTableHeader, FilteredColumns } from '@components/TableContextMenuWraper';
import { orderBySimple } from '@components/orderByFunc';

export enum MessageStatus {
    Pending = 0,
    Delivered = 1,
    Failed = 2,
    canceled = 3,
    aborted = 4,
    PendingThirdParty = 5,
    FailedThirdParty = 6,
    rejectedThirdParty = 7,
}

interface ReservationNotificationsProps {
    reservation: Reservation;
    history: any;
}

function ReservationNotifications(props: ReservationNotificationsProps) {
    const [reservationNotifications, setReservationNotifications] = React.useState<MessageBrokerInformationModel[]>([]);
    const [selfServiceCheckInId, setSelfServiceCheckInId] = React.useState(null as any);
    const [newInfo] = React.useState(false);
    const [filters, setFilters] = React.useState([] as Filters[]);
    const [sort, setSort] = React.useState({} as any);
    const [headersFilter, setHeadersFilter] = React.useState([] as string[]);
    const [showContextMenu, setShowContextMenu] = React.useState(null as string | null);
    const { reservation, history } = props;
    const otherSettings = useStore.getState().otherSettings;

    const headers = [
        {
            filterKey: 'timestamp',
            headerTitle: translate('Date'),
            label: 'timestamp',
            type: 'datetime',
        },
        {
            filterKey: 'subject',
            headerTitle: translate('Title'),
            label: 'subject',
            type: '',
        },
        {
            filterKey: 'message',
            headerTitle: translate('Message'),
            label: 'message',
            type: '',
        },
        {
            filterKey: 'statusFlag',
            headerTitle: translate('Status'),
            label: 'statusFlag',
            type: 'enum',
            enumDescription: MessageStatus,
        },
    ];

    const loadDataSetState = async () => {
        if (reservation?.SelfServiceCheckIn?.id) {
            const _selfServiceCheckInId = reservation?.SelfServiceCheckIn?.id;
            setSelfServiceCheckInId(_selfServiceCheckInId);
        }
        if (reservation && reservation.id) {
            const _reservationNotifications = await getReservationNotifications(reservation.id, selfServiceCheckInId);
            setReservationNotifications(_reservationNotifications);
        }
    };

    React.useEffect(() => {
        const loadData = async () => {
            await loadDataSetState();
        };
        loadData().catch((err) => {
            console.log(err);
        });
        // eslint-disable-next-line
    }, [reservation?.id, selfServiceCheckInId, newInfo]);

    const canSendNotification = (model: string | undefined): boolean => {
        if (!model) return false;
        if (model === 'SelfServiceCheckIn') return !!selfServiceCheckInId;
        if (model === 'Reservation') return !!otherSettings?.sendReservationConfirmationToGuest;
        if (model === '_Reservation') return !!otherSettings?.reminderEmail && otherSettings.reminderEmail > 0;
        return false;
    };

    const sendNotificationAgain = async (model: string | undefined) => {
        if (!model || !canSendNotification(model)) return;

        const textContent =
            model === 'SelfServiceCheckIn'
                ? 'Are you sure you want to send self check in email?'
                : 'Are you sure you want to send email again?';
        setModalContent(
            <ConfirmModal
                actionForYes={async () => {
                    try {
                        await sendNotificationOnAction(model, reservation?.id);
                        await loadDataSetState();
                    } catch (err) {
                        console.warn(err);
                    }
                }}
                content={translate(textContent)}
            />,
            translate('Confirm'),
            false,
            'modal-md'
        );
    };

    let notificationsList = [...reservationNotifications].sort((a, b) => a.timestamp - b.timestamp);

    if (sort) {
        for (const key in sort) {
            const val = sort[key];
            notificationsList = orderBySimple(notificationsList, key as keyof MessageBrokerInformationModel, val);
        }
    }

    const filteredNotifications = filterListItems(notificationsList, filters);

    if (filteredNotifications.length === 0) {
        return <small>{translate('List is empty')}</small>;
    }

    return (
        <div className="">
            <ListTableHeader
                sort={sort}
                setSort={setSort}
                headers={headers}
                headersFilter={headersFilter}
                setHeadersFilter={setHeadersFilter}
                listArray={notificationsList}
                showContextMenu={showContextMenu}
                setShowContextMenu={setShowContextMenu}
                filters={filters}
                setFilters={setFilters}
                setPage={() => {}}
                history={history}
            >
                <div></div>
            </ListTableHeader>

            {filteredNotifications?.map((t: MessageBrokerInformationModel, index: number) => {
                return (
                    <div key={index} className="custom-list-item">
                        <FilteredColumns
                            headersFilter={headersFilter}
                            headers={headers}
                            listItem={t}
                            history={history}
                        />
                        <div
                            className={`button-white-w-icon-only ${!canSendNotification(t.model) ? 'invisible' : ''}`}
                            onClick={() => canSendNotification(t.model) && sendNotificationAgain(t.model)}
                        >
                            <i className="fa fa-envelope"></i>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default ReservationNotifications;
